import './TopBar.scss';
import { useContext } from 'react';

import { GlobalStateContext } from '../../../contexts/GlobalState';
import { globalStateActionType } from '../../../contexts/GlobalState/reducer';
import { configService } from '../../../services/configService';
import { BrandGlyph } from '../../icons/BrandGlyph';
import { CwIcon } from '../../icons/CwIcon';

function envFromAppId(appId) {
    if (!appId) return { code: 'LOCAL', kind: 'dev' };
    const upper = appId.toUpperCase();
    if (upper.includes('PROD') || /CAP\d+/i.test(upper)) return { code: upper, kind: 'prod' };
    if (upper.includes('QA') || /CAQ\d+/i.test(upper)) return { code: upper, kind: 'qa' };
    return { code: upper, kind: 'dev' };
}

function getInitials(identityToken) {
    if (!identityToken) return '?';
    const first = (identityToken.givenName || '').trim().charAt(0);
    const last = (identityToken.familyName || '').trim().charAt(0);
    const initials = (first + last).toUpperCase();
    if (initials) return initials;
    if (identityToken.email) {
        return identityToken.email.trim().charAt(0).toUpperCase() || '?';
    }
    return '?';
}

function getDisplayName(identityToken) {
    if (!identityToken) return '';
    const given = (identityToken.givenName || '').trim();
    const family = (identityToken.familyName || '').trim();
    const full = [given, family].filter(Boolean).join(' ');
    return full || identityToken.email || '';
}

export const TopBar = () => {
    const [state, dispatch] = useContext(GlobalStateContext);

    function signOut() {
        dispatch({ type: globalStateActionType.SIGNOUT });
    }

    function setDarkMode(isDark) {
        if (state.isDarkMode === isDark) return;
        dispatch({ type: globalStateActionType.TOGGLE_DARK_MODE });
    }

    const isAuthenticated = !!(state.identityToken && state.accessToken);
    const env = envFromAppId(configService.appId);
    const buildNumber = process.env.REACT_APP_BUILD_NUMBER || 'local';

    return (
        <header className="cw-TopBar" role="banner">
            <div className="cw-TopBar__brand">
                <BrandGlyph className="cw-TopBar__brand-glyph" />
                <span className="cw-TopBar__brand-word">Collabspace</span>
                <span className="cw-TopBar__brand-sub">Admin</span>
            </div>

            {isAuthenticated ? (
                <>
                    <span className={`cw-TopBar__env cw-TopBar__env--${env.kind}`}>
                        <span className="cw-TopBar__env-dot" />
                        {env.code}
                    </span>
                    <span className="cw-TopBar__build">build {buildNumber}</span>

                    <span className="cw-TopBar__spacer" />

                    <div
                        className="cw-TopBar__theme"
                        role="radiogroup"
                        aria-label="Theme"
                    >
                        <button
                            type="button"
                            role="radio"
                            aria-checked={!state.isDarkMode}
                            aria-label="Light theme"
                            onClick={() => setDarkMode(false)}
                        >
                            <CwIcon name="sun" size={16} strokeWidth={1.6} />
                        </button>
                        <button
                            type="button"
                            role="radio"
                            aria-checked={state.isDarkMode}
                            aria-label="Dark theme"
                            onClick={() => setDarkMode(true)}
                        >
                            <CwIcon name="moon" size={16} strokeWidth={1.6} />
                        </button>
                    </div>

                    <div className="cw-TopBar__user" tabIndex={0}>
                        <span className="cw-TopBar__avatar" aria-hidden="true">
                            {getInitials(state.identityToken)}
                        </span>
                        <span className="cw-TopBar__user-meta">
                            <span className="cw-TopBar__user-name">
                                {getDisplayName(state.identityToken)}
                            </span>
                            <span className="cw-TopBar__user-email">
                                {state.identityToken?.email || ''}
                            </span>
                        </span>
                    </div>

                    <button
                        type="button"
                        className="cw-TopBar__signout"
                        onClick={signOut}
                    >
                        Sign out
                    </button>
                </>
            ) : null}
        </header>
    );
};
